import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box } from "@mui/material";
import SnackBar from "../../../components/SnackBar";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { GridLoader } from "react-spinners";
import { buscarSumarizacaoApuracao, atualizarBaseLegalSumarizacao, buscarBasesLegais, exportarSumarizacaoApuracao as exportarSumarizacaoApuracaoService } from "../../../services/apuracao.service";
import { listarProjetosDoCliente, salvarProjeto } from "../../../services/projetos-apuracao.service";
import { lerParametrosProjeto, montarUrlApuracao } from "../../ProjetosApuracao/utils/url.util";
import { chavesDasLinhas, resumoDaSelecao } from "../../ProjetosApuracao/utils/selecao.util";
import { exibirCodRelatorio, fmtMesAno, formatarCnpj } from "../utils";
import { agregarPorConta, labelConta } from "./utils/agregarPorConta.util";
import { filtrarLinhasSumarizacao, competenciaRangeFromLinhas } from "./utils/filtrarLinhas.util";
import { montarSubtituloDetalhe } from "./utils/formatarFiltrosExclusao.util";
import DashboardCards from "./components/DashboardCards";
import FiltrosSumarizacao from "./components/FiltrosSumarizacao";
import TabelaContas from "./components/TabelaContas";
import SectionToolbar from "./components/SectionToolbar";
import BreadcrumbApuracao from "../components/BreadcrumbApuracao";
import ModalExportarLancamentos from "./components/ModalExportarLancamentos";
import ModalSalvarProjeto from "../../ProjetosApuracao/components/ModalSalvarProjeto";
import SeletorProjetosCliente from "../../ProjetosApuracao/components/SeletorProjetosCliente";
import useExportacaoAssincrona from "./hooks/useExportacaoAssincrona";
import ModalReprocessarSumarizacao from "../ModalReprocessarSumarizacao";
import ModalAtualizarSelicApuracao from "../ModalAtualizarSelicApuracao";
import styles from "./detalhe.styles.module.css";
import {
  usePermissoes,
  CHAVES_APURACOES_EDIT,
  CHAVES_APURACOES_EXPORT,
} from "../../../config/Context/PermissoesContext";

function filtrosPadrao(ap, linhas) {
  const { min, max } = competenciaRangeFromLinhas(linhas);
  return {
    competenciaInicio: min || "",
    competenciaFim: max || "",
    buscaConta: "",
    baseLegalSelecionadas: [],
    contasSelecionadas: [],
    baseMin: "",
    baseMax: "",
    pisMin: "",
    pisMax: "",
    cofinsMin: "",
    cofinsMax: "",
  };
}

export default function ApuracaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pode } = usePermissoes();
  const podeEditar = pode(CHAVES_APURACOES_EDIT);
  const podeExportar = pode(CHAVES_APURACOES_EXPORT);
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
  const [dados, setDados] = useState(null);
  const [filtros, setFiltros] = useState(filtrosPadrao());
  const [objSnackBar, setObjSnackBar] = useState({ type: "", msg: "" });
  const [baseLegalSalvando, setBaseLegalSalvando] = useState({});
  const [basesLegais, setBasesLegais] = useState([]);
  const [modalExportarLancamentos, setModalExportarLancamentos] = useState(false);
  const [modalReprocessar, setModalReprocessar] = useState(false);
  const [modalAtualizarSelic, setModalAtualizarSelic] = useState(false);
  const [projetosCliente, setProjetosCliente] = useState([]);
  const [modalSalvarProjeto, setModalSalvarProjeto] = useState(false);
  const [salvandoProjeto, setSalvandoProjeto] = useState(false);

  // TXM-558 — `?pf=` reaplica os filtros salvos de um projeto (uma vez, depois da primeira carga) e
  // `?projeto=` abre o modo consulta dos itens utilizados por aquele projeto aprovado.
  const { filtros: filtrosSalvosUrl, idProjeto: projetoFiltro } = useMemo(
    () => lerParametrosProjeto(location.search),
    [location.search]
  );
  const pfBruto = new URLSearchParams(location.search).get("pf");
  const pfNonce = location.state?.pfNonce;
  const ultimoPfAplicado = useRef({ pf: null, nonce: undefined });

  useEffect(() => {
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro("");
      const res = await buscarSumarizacaoApuracao(
        id,
        projetoFiltro ? { projeto: projetoFiltro } : undefined
      );
      if (!ativo) return;

      if (!res.sucesso) {
        setErro(res.mensagem || "Sumarização indisponível para esta apuração.");
        setLoading(false);
        return;
      }

      setDados(res);
      setFiltros(filtrosPadrao(res.apuracao, res.linhas));
      setBaseLegalSalvando({});
      setLoading(false);
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [id, navigate, projetoFiltro]);

  // Reaplica os filtros salvos de um projeto (`?pf=`) por cima do padrão, depois que a carga termina.
  // Só reaplica quando o `pf` muda ou quando o usuário escolhe de novo um projeto em "Projetos deste
  // cliente" (`pfNonce`); voltar do drill-down não pode sobrescrever os filtros que ele tinha.
  useEffect(() => {
    if (!dados || !filtrosSalvosUrl) return;
    const anterior = ultimoPfAplicado.current;
    const mudou = anterior.pf !== pfBruto || (pfNonce !== undefined && pfNonce !== anterior.nonce);
    if (!mudou) return;
    ultimoPfAplicado.current = { pf: pfBruto, nonce: pfNonce };
    setFiltros({ ...filtrosPadrao(dados.apuracao, dados.linhas), ...filtrosSalvosUrl });
  }, [dados, filtrosSalvosUrl, pfBruto, pfNonce]);

  // Projetos do cliente (TXM-558): alimenta o seletor "Projeto" e, depois, "Projetos deste cliente".
  // Falha aqui não bloqueia o detalhe.
  const cnpjApuracao = dados?.apuracao?.cnpj;
  const carregarProjetosCliente = useCallback(async () => {
    if (!cnpjApuracao) return;
    const res = await listarProjetosDoCliente({ cnpj: cnpjApuracao, tipo: "pis-cofins" });
    setProjetosCliente(res.sucesso && Array.isArray(res.projetos) ? res.projetos : []);
  }, [cnpjApuracao]);

  useEffect(() => {
    carregarProjetosCliente();
  }, [carregarProjetosCliente]);

  const projetosAprovados = useMemo(
    () => projetosCliente.filter((projeto) => projeto.status === "APROVADO"),
    [projetosCliente]
  );
  const projetosEmPrevia = useMemo(
    () => projetosCliente.filter((projeto) => projeto.status === "EM_PREVIA"),
    [projetosCliente]
  );

  const handleProjetoChange = useCallback(
    (idProjeto) => {
      navigate(idProjeto ? `${location.pathname}?projeto=${idProjeto}` : location.pathname, {
        replace: true,
      });
    },
    [location.pathname, navigate]
  );

  // Catálogo de bases legais: independente da apuração, carregado uma vez por tela.
  // Falha aqui não bloqueia o detalhe — a coluna fica sem opções e o service já loga.
  useEffect(() => {
    let ativo = true;
    buscarBasesLegais("CONCILIACAO_SPED").then((res) => {
      if (!ativo) return;
      setBasesLegais(res.basesLegais || []);
    });
    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    const stateId = location.state?.apuracaoId;
    const filtrosUi = location.state?.filtrosUi;
    if (!filtrosUi || stateId !== id) return;

    setFiltros(filtrosUi);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [id, location.pathname, location.search, location.state?.apuracaoId, location.state?.filtrosUi, navigate]);

  const handleBaseLegalChange = useCallback(
    async (ecdConta, baseLegalId) => {
      const chave = String(ecdConta || "");
      const linhasAntes = dados?.linhas;
      const descricao =
        baseLegalId == null
          ? null
          : basesLegais.find((b) => b.id === baseLegalId)?.descricao ?? null;

      // Update otimista: aplica também `compoeProjeto`, que o backend deriva. Sem isso a
      // tela mostraria a base legal preenchida e a conta fora do projeto até o refetch.
      setDados((prev) => {
        if (!prev?.linhas) return prev;
        return {
          ...prev,
          linhas: prev.linhas.map((linha) =>
            String(linha.ecdContaContabil || "") === chave
              ? {
                  ...linha,
                  baseLegalId,
                  baseLegalDescricao: descricao,
                  compoeProjeto: baseLegalId != null,
                }
              : linha
          ),
        };
      });
      setBaseLegalSalvando((prev) => ({ ...prev, [chave]: true }));

      const res = await atualizarBaseLegalSumarizacao(id, {
        ecdContaContabil: chave,
        baseLegalId,
      });

      setBaseLegalSalvando((prev) => {
        const next = { ...prev };
        delete next[chave];
        return next;
      });

      if (!res.sucesso) {
        setDados((prev) => {
          if (!prev?.linhas || !linhasAntes) return prev;
          return { ...prev, linhas: linhasAntes };
        });
        setObjSnackBar({
          type: "error",
          msg: res.mensagem || "Não foi possível salvar a base legal.",
        });
        return;
      }

      setObjSnackBar({
        type: "success",
        msg:
          baseLegalId == null
            ? "Base legal removida — a conta saiu do projeto."
            : "Base legal salva com sucesso.",
      });
    },
    [basesLegais, dados?.linhas, id]
  );

  // Um hook por variante (desenho 2026-10-02, 5/grupo C): cada uma é um job independente
  // no servidor e pode rodar junto com as outras. `filtros` é lido no momento do clique (o
  // hook sempre usa o `enfileirar` do último render).
  const notificarExportacao = ({ severity, msg }) => setObjSnackBar({ type: severity, msg });
  const exportacaoAgrupado = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "agrupado",
    enfileirar: () => exportarSumarizacaoApuracaoService(id, { tipo: "agrupado", filtros }),
    onFinalizar: notificarExportacao,
  });
  const exportacaoLancamentos = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "lancamentos",
    enfileirar: ({ formato, modoXlsx }) =>
      exportarSumarizacaoApuracaoService(id, { tipo: "lancamentos", filtros, formato, modoXlsx }),
    onFinalizar: notificarExportacao,
  });
  const exportacaoResumoGeral = useExportacaoAssincrona({
    apuracaoId: id,
    chave: "resumo_geral",
    enfileirar: () => exportarSumarizacaoApuracaoService(id, { tipo: "resumo_geral", filtros }),
    onFinalizar: notificarExportacao,
  });

  const iniciarExportacaoLancamentos = exportacaoLancamentos.iniciar;
  const handleConfirmarExportLancamentos = useCallback(
    ({ formato, modoXlsx }) => {
      // Não espera o job: o modal fecha e o progresso segue no botão Exportar.
      iniciarExportacaoLancamentos({ formato, modoXlsx });
      setModalExportarLancamentos(false);
    },
    [iniciarExportacaoLancamentos]
  );

  const contasDisponiveis = useMemo(() => {
    const map = new Map();
    for (const linha of dados?.linhas || []) {
      const cod = String(linha.ecdContaContabil || "").trim();
      if (!cod) continue;
      const existente = map.get(cod);
      if (existente) {
        if (linha.contaResultado) existente.contaResultado = true;
        continue;
      }
      const nome = linha.contaContabil || "";
      map.set(cod, {
        codigo: cod,
        label: labelConta(cod, nome),
        contaResultado: Boolean(linha.contaResultado),
      });
    }
    return Array.from(map.values());
  }, [dados?.linhas]);

  const linhasFiltradas = useMemo(
    () => filtrarLinhasSumarizacao(dados?.linhas || [], filtros),
    [dados?.linhas, filtros]
  );

  const contasAgregadas = useMemo(
    () => agregarPorConta(linhasFiltradas),
    [linhasFiltradas]
  );

  const totalLancamentos = useMemo(
    () => linhasFiltradas.reduce((s, l) => s + (Number(l.qtdLancamentos) || 0), 0),
    [linhasFiltradas]
  );

  const limparFiltros = () => setFiltros(filtrosPadrao(dados?.apuracao, dados?.linhas));

  const selecaoProjeto = useMemo(() => resumoDaSelecao(linhasFiltradas), [linhasFiltradas]);

  const handleSalvarProjeto = async ({ sobrescreverIdProjeto }) => {
    setSalvandoProjeto(true);
    const res = await salvarProjeto({
      idRelatorioApuracao: Number(id),
      filtros,
      chaves: chavesDasLinhas(linhasFiltradas),
      sobrescreverIdProjeto,
    });
    setSalvandoProjeto(false);

    if (!res.sucesso) {
      setObjSnackBar({ type: "error", msg: res.mensagem || "Não foi possível salvar o projeto." });
      return;
    }

    setModalSalvarProjeto(false);
    const descartados = Array.isArray(res.descartados) ? res.descartados.length : 0;
    const aviso =
      descartados > 0
        ? ` ${descartados} ${descartados === 1 ? "item não entrou" : "itens não entraram"} no projeto (já utilizados ou sem base legal).`
        : "";
    setObjSnackBar({
      type: descartados > 0 ? "warning" : "success",
      msg: `Projeto ${res.codigo} ${res.criado ? "criado" : "atualizado"} com sucesso!${aviso}`,
    });
    carregarProjetosCliente();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <GridLoader color="#18316e" />
      </Box>
    );
  }

  if (erro || !dados) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{erro || "Não foi possível carregar a sumarização."}</Alert>
        <button
          type="button"
          className={styles.linkVoltar}
          style={{ marginTop: 16 }}
          onClick={() => navigate("/relatorios/apuracoes")}
        >
          ← Voltar para Apurações
        </button>
      </Box>
    );
  }

  const codigoProjetoFiltro =
    projetosAprovados.find((projeto) => projeto.idProjeto === projetoFiltro)?.codigo ||
    `#${projetoFiltro}`;

  const ap = dados.apuracao || {};
  const apComId = { ...ap, id: ap.id ?? id };
  const tipoLabel = exibirCodRelatorio(ap.tipo);
  const subtitulo = montarSubtituloDetalhe(apComId, {
    formatarCnpj,
    exibirCodRelatorio,
    fmtMesAno,
  });

  // Mesma moldura da tela de Usuários (e dos Lançamentos): breadcrumb clicável no lugar do
  // botão "← Voltar", e o conteúdo da apuração num card branco dentro do canvas cinza.
  return (
    <>
      <BreadcrumbApuracao
        itens={[
          { label: "Apurações", onClick: () => navigate("/relatorios/apuracoes") },
          { label: `Apuração #${id}` },
        ]}
      >
      <div className={styles.container} style={{ border: "1px solid #e0e0e0", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" }}>
        <header className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>
              Apuração PIS/COFINS — {ap.empresa || "—"}
            </h1>
            <p className={styles.pageSubtitle}>{subtitulo}</p>
            <span className={styles.badgeTipo}>{tipoLabel} · Contas e lançamentos</span>
          </div>
          <div className={styles.headerActions}>
            <SeletorProjetosCliente
              projetos={projetosCliente}
              onSelecionar={(projeto) =>
                navigate(montarUrlApuracao(projeto), { state: { pfNonce: Date.now() } })
              }
            />
            {podeEditar && !projetoFiltro && (
              <button
                type="button"
                className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                onClick={() => setModalSalvarProjeto(true)}
              >
                Salvar Projeto
              </button>
            )}
            {podeEditar && (
              <button
                type="button"
                className={styles.btnHeader}
                onClick={() => setModalAtualizarSelic(true)}
              >
                Atualizar Selic
              </button>
            )}
            {podeEditar && (
              <button
                type="button"
                className={`${styles.btnHeader} ${styles.btnHeaderPrimary}`}
                onClick={() => setModalReprocessar(true)}
              >
                Reprocessar
              </button>
            )}
          </div>
        </header>

        <DashboardCards
          linhas={linhasFiltradas}
          totalLinhasOriginais={dados?.linhas?.length ?? 0}
        />

        <div className={styles.sectionListHeader}>
          <h2 className={styles.sectionTitle}>Contas contábeis</h2>
          <SectionToolbar
            competenciaInicio={filtros.competenciaInicio}
            competenciaFim={filtros.competenciaFim}
            totalLancamentos={totalLancamentos}
            totalContas={contasAgregadas.length}
            progressos={{
              agrupado: exportacaoAgrupado,
              lancamentos: exportacaoLancamentos,
              resumo_geral: exportacaoResumoGeral,
            }}
            podeExportar={podeExportar}
            onExportAgrupado={() => exportacaoAgrupado.iniciar()}
            onExportLancamentos={() => setModalExportarLancamentos(true)}
            onExportResumoGeral={() => exportacaoResumoGeral.iniciar()}
          />
        </div>

        <FiltrosSumarizacao
          filtros={filtros}
          onChange={setFiltros}
          contasDisponiveis={contasDisponiveis}
          basesLegais={basesLegais}
          onLimpar={limparFiltros}
          projetosAprovados={projetosAprovados}
          projetoSelecionado={projetoFiltro ? String(projetoFiltro) : ""}
          onProjetoChange={handleProjetoChange}
        />

        {projetoFiltro && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Consulta dos itens do projeto {codigoProjetoFiltro}. Não é possível editar nem salvar
            projeto neste modo.
          </Alert>
        )}

        <TabelaContas
          contas={contasAgregadas}
          totalLancamentos={totalLancamentos}
          basesLegais={basesLegais}
          baseLegalSalvando={baseLegalSalvando}
          podeEditar={podeEditar}
          somenteLeitura={Boolean(projetoFiltro)}
          onBaseLegalChange={handleBaseLegalChange}
          onDetalhe={(row) =>
            navigate(
              `/relatorios/apuracoes/${id}/detalhe/conta?ecd=${encodeURIComponent(
                row.ecdContaContabil || ""
              )}${projetoFiltro ? `&projeto=${projetoFiltro}` : ""}`,
              { state: { filtrosUi: filtros, apuracaoId: id } }
            )
          }
        />
      </div>
      </BreadcrumbApuracao>

      <SnackBar objSnackBar={objSnackBar} setObjSnackBar={setObjSnackBar} />

      <ModalExportarLancamentos
        open={modalExportarLancamentos}
        totalLancamentos={totalLancamentos}
        confirmando={exportacaoLancamentos.ativo}
        onClose={() => setModalExportarLancamentos(false)}
        onConfirm={handleConfirmarExportLancamentos}
      />

      <ModalSalvarProjeto
        open={modalSalvarProjeto}
        onClose={() => setModalSalvarProjeto(false)}
        empresa={{ razaoSocial: ap.empresa, cnpj: ap.cnpj }}
        periodoLabel={`${fmtMesAno(filtros.competenciaInicio)} – ${fmtMesAno(filtros.competenciaFim)}`}
        selecao={selecaoProjeto}
        projetosEmPrevia={projetosEmPrevia}
        salvando={salvandoProjeto}
        onConfirmar={handleSalvarProjeto}
      />

      <ModalReprocessarSumarizacao
        open={modalReprocessar}
        idApuracao={id}
        apuracao={ap}
        onClose={() => setModalReprocessar(false)}
        onSuccess={(msg) => {
          setObjSnackBar({ type: "success", msg });
          navigate("/relatorios/apuracoes");
        }}
        onError={(msg) => setObjSnackBar({ type: "error", msg })}
      />

      <ModalAtualizarSelicApuracao
        open={modalAtualizarSelic}
        idApuracao={id}
        apuracao={ap}
        onClose={() => setModalAtualizarSelic(false)}
        onSuccess={(msg) => {
          setObjSnackBar({ type: "success", msg });
          navigate("/relatorios/apuracoes");
        }}
        onError={(msg) => setObjSnackBar({ type: "error", msg })}
      />
    </>
  );
}
